/**
 * One visit, kept current by the server's event stream (ADR-0024).
 *
 * The page reads a snapshot, then WAITS on `/events`: every event means "the
 * visit changed", so it re-reads the snapshot (the snapshot, not the event,
 * decides what is shown). EventSource reconnects by itself and resumes from the
 * last event id. The page never polls the vending server; the only extra read
 * is when the tab becomes visible again, e.g. back from a payment app.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { eventsUrl, getVisit } from "./api";
import { EVENT_KINDS, TERMINAL, type VendSession } from "./vend";

export type Link = "connecting" | "live" | "reconnecting" | "closed";

export interface VisitState {
  visit: VendSession | null;
  error: unknown;
  link: Link;
  refresh: () => Promise<void>;
  replace: (v: VendSession) => void;
}

export function useVisit(id: string): VisitState {
  const [visit, setVisit] = useState<VendSession | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [link, setLink] = useState<Link>("connecting");
  const inFlight = useRef<Promise<void> | null>(null);
  const again = useRef(false);

  // Coalesce: a burst of events becomes at most one read in flight plus one more.
  const refresh = useCallback(async (): Promise<void> => {
    if (inFlight.current) {
      again.current = true;
      return inFlight.current;
    }
    const run = (async () => {
      do {
        again.current = false;
        try {
          setVisit(await getVisit(id));
          setError(null);
        } catch (e) {
          setError(e);
        }
      } while (again.current);
    })();
    inFlight.current = run;
    try {
      await run;
    } finally {
      inFlight.current = null;
    }
  }, [id]);

  const terminal = visit ? TERMINAL.has(visit.state) : false;

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (terminal) {
      setLink("closed");
      return;
    }
    const source = new EventSource(eventsUrl(id));
    const onEvent = () => void refresh();
    for (const kind of EVENT_KINDS) source.addEventListener(kind, onEvent);
    // Terminal: the server says so and closes. Do not reconnect.
    source.addEventListener("closed", () => {
      source.close();
      setLink("closed");
      void refresh();
    });
    source.onopen = () => setLink("live");
    source.onerror = () => {
      setLink(source.readyState === EventSource.CLOSED ? "closed" : "reconnecting");
    };
    return () => source.close();
  }, [id, terminal, refresh]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [refresh]);

  return { visit, error, link, refresh, replace: setVisit };
}
